import { useId, useState } from 'react';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  WORKBOOK_ENTITY_FILTER_OPERATORS,
  WORKBOOK_MAX_ENTITY_FILTERS,
  workbookEntityFilterProblem,
  type WorkbookCatalogEntry,
  type WorkbookColumnType,
  type WorkbookEntityFilter,
} from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFilterOperatorLabels } from './WorksheetFilters';
import { filterTakesValue } from './worksheetFilterOperators';
import { isoToLocalInput, localInputToIso } from './workbookDateTimeInput';

type EntityColumn = NonNullable<WorkbookCatalogEntry['columns']>[number];
type Operator = WorkbookEntityFilter['operator'];

/** A true/false column compares only for equality. */
const BOOLEAN_OPERATORS: readonly Operator[] = [
  'eq',
  'neq',
  'is-null',
  'is-not-null',
];
/** The comparisons a column of this type offers. */
export function entityFilterOperators(
  type: WorkbookColumnType | undefined,
): readonly Operator[] {
  if (type === 'boolean') return BOOLEAN_OPERATORS;
  if (type === 'text') return WORKBOOK_ENTITY_FILTER_OPERATORS;
  return WORKBOOK_ENTITY_FILTER_OPERATORS.filter(
    (operator) => operator !== 'contains',
  );
}

/** Why a drafted filter can't be applied yet, by the same rule the server applies, or null. */
function draftProblem(
  filter: WorkbookEntityFilter,
  type: WorkbookColumnType | undefined,
) {
  return type && filterTakesValue(filter.operator)
    ? workbookEntityFilterProblem(filter, type)
    : null;
}

/**
 * An Entity Source's filters, edited as a draft and applied together, since each
 * application reads the entity again.
 */
export function WorkbookEntityFilters({
  filters,
  columns,
  disabled,
  onApply,
}: {
  filters: readonly WorkbookEntityFilter[];
  columns: readonly EntityColumn[];
  disabled: boolean;
  onApply: (filters: WorkbookEntityFilter[]) => void;
}) {
  const gt = useGT();
  const labels = useFilterOperatorLabels();
  const [draft, setDraft] = useState<WorkbookEntityFilter[]>([...filters]);
  const changed = JSON.stringify(draft) !== JSON.stringify(filters);
  const typeOf = (column: string) =>
    columns.find((candidate) => candidate.name === column)?.type;
  const blocked = draft.some(
    (filter) => draftProblem(filter, typeOf(filter.column)) !== null,
  );
  function update(index: number, patch: Partial<WorkbookEntityFilter>) {
    setDraft((latest) =>
      latest.map((filter, i) => {
        if (i !== index) return filter;
        const next = { ...filter, ...patch };
        // A new column keeps the comparison only when that column offers it.
        return entityFilterOperators(typeOf(next.column)).includes(
          next.operator,
        )
          ? next
          : { ...next, operator: 'eq', value: '' };
      }),
    );
  }
  return (
    <div className="space-y-2">
      {draft.length === 0 && (
        <p>
          <T context="workbook entity no filters">
            Reads every row. Add a filter to read only the rows you need.
          </T>
        </p>
      )}
      {draft.map((filter, index) => {
        const type = typeOf(filter.column);
        return (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <Select
              disabled={disabled}
              value={filter.column || null}
              onValueChange={(column) => update(index, { column })}
            >
              <SelectTrigger
                className="min-w-40"
                aria-label={gt('Filter column', {
                  context: 'workbook entity filter column',
                })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {columns.map((column) => (
                  <SelectItem key={column.name} value={column.name}>
                    {column.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              disabled={disabled}
              value={filter.operator}
              onValueChange={(value) => {
                const operator = entityFilterOperators(type).find(
                  (candidate) => candidate === value,
                );
                if (operator) update(index, { operator });
              }}
            >
              <SelectTrigger
                className="min-w-36"
                aria-label={gt('Filter comparison', {
                  context: 'workbook entity filter operator',
                })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {entityFilterOperators(type).map((operator) => (
                  <SelectItem key={operator} value={operator}>
                    {labels[operator]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterTakesValue(filter.operator) && (
              <FilterValue
                type={type}
                value={filter.value}
                disabled={disabled}
                // A value not yet typed is incomplete, not wrong.
                invalid={
                  filter.value.trim() !== '' &&
                  draftProblem(filter, type) !== null
                }
                onChange={(value) => update(index, { value })}
              />
            )}
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              disabled={disabled}
              aria-label={gt('Remove filter', {
                context: 'workbook entity remove filter',
              })}
              onClick={() =>
                setDraft((latest) => latest.filter((_, i) => i !== index))
              }
            >
              <TrashIcon />
            </Button>
          </div>
        );
      })}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={
            disabled ||
            columns.length === 0 ||
            draft.length >= WORKBOOK_MAX_ENTITY_FILTERS
          }
          onClick={() =>
            setDraft((latest) => [
              ...latest,
              { column: columns[0]?.name ?? '', operator: 'eq', value: '' },
            ])
          }
        >
          <PlusIcon />
          <T context="workbook entity add filter">Add filter</T>
        </Button>
        <Button
          size="sm"
          disabled={disabled || !changed || blocked}
          onClick={() => onApply(draft)}
        >
          <T context="workbook entity apply filters">Apply filters</T>
        </Button>
      </div>
    </div>
  );
}

/** What a filter value must be, for a column of each typed Declared type. */
function useExpectedValue(): Record<
  Exclude<WorkbookColumnType, 'text'>,
  string
> {
  const gt = useGT();
  const context = 'workbook entity filter value of the wrong type';
  return {
    decimal: gt('Enter a number', { context }),
    date: gt('Enter a date', { context }),
    datetime: gt('Enter a date and time', { context }),
    boolean: gt('Choose true or false', { context }),
  };
}

function FilterValue({
  type,
  value,
  disabled,
  invalid,
  onChange,
}: {
  type: WorkbookColumnType | undefined;
  value: string;
  disabled: boolean;
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  const gt = useGT();
  const expected = useExpectedValue();
  const label = gt('Filter value', { context: 'workbook entity filter value' });
  const errorId = useId();
  const showsError = invalid && type !== undefined && type !== 'text';
  // One tree whether or not it's invalid, so typing never remounts the field.
  return (
    <span className="flex flex-col gap-0.5">
      <FilterValueInput
        type={type}
        value={value}
        label={label}
        disabled={disabled}
        invalid={invalid}
        describedBy={showsError ? errorId : undefined}
        onChange={onChange}
      />
      {showsError && (
        <span id={errorId} role="alert" className="text-xs text-destructive">
          {expected[type]}
        </span>
      )}
    </span>
  );
}

function FilterValueInput({
  type,
  value,
  label,
  disabled,
  invalid,
  describedBy,
  onChange,
}: {
  type: WorkbookColumnType | undefined;
  value: string;
  label: string;
  disabled: boolean;
  invalid: boolean;
  describedBy: string | undefined;
  onChange: (value: string) => void;
}) {
  const gt = useGT();
  if (type === 'boolean')
    return (
      <Select
        disabled={disabled}
        value={value || null}
        onValueChange={onChange}
      >
        <SelectTrigger
          className="min-w-28"
          aria-label={label}
          aria-invalid={invalid}
          aria-describedby={describedBy}
        >
          <SelectValue
            placeholder={gt('Choose', {
              context: 'workbook entity filter boolean placeholder',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">
            {gt('True', { context: 'workbook entity filter boolean value' })}
          </SelectItem>
          <SelectItem value="false">
            {gt('False', { context: 'workbook entity filter boolean value' })}
          </SelectItem>
        </SelectContent>
      </Select>
    );
  if (type === 'datetime')
    return (
      <Input
        className="h-8 w-52 text-xs"
        aria-label={label}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        disabled={disabled}
        type="datetime-local"
        value={isoToLocalInput(value)}
        onChange={(event) => onChange(localInputToIso(event.target.value))}
      />
    );
  return (
    <Input
      className="h-8 w-40 text-xs"
      aria-label={label}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      disabled={disabled}
      type={type === 'date' ? 'date' : 'text'}
      inputMode={type === 'decimal' ? 'decimal' : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
